<nav class="sticky top-0 z-40 bg-basic">
  <div class="flex">
    <!-- Logo -->
    <div class="w-21.25 h-21.25 bg-accent grid place-items-center shrink-0">
      <img src="logo.svg" [attr.alt]="'NAVBAR.LOGO_ALT' | translate" width="39" height="46" />
    </div>
    <!-- Navbar content -->
    <div
      class="flex items-start justify-between gap-layout-1 pl-layout-2 md:pl-layout-5 pr-component-7 pt-layout-2 flex-1 min-w-0 border-b border-subtle"
    >
      <!-- Left: title + desktop nav links -->
      <div class="flex gap-x-layout-5 h-full">
        <p class="text-body-s-bold text-dominant">{{ titleKey() | translate }}</p>
        <!-- Desktop nav links (hidden on mobile) -->
        <div class="hidden md:flex gap-layout-3 text-body-m-bold">
          @for (route of routes(); track route.path) {
            <a
              class="flex flex-col justify-between cursor-pointer hover:text-accent-recessive h-full"
              [class.text-dominant]="route.isActive"
              [routerLink]="route.path"
            >
              <span>{{ route.title | translate }}</span>
              @if (route.isActive) {
                <hr class="border-2 rounded-full" aria-hidden="true" />
              }
            </a>
          }
        </div>
      </div>
      <!-- Right: user info + hamburger -->
      <div class="flex gap-component-4 items-center shrink-0">
        <button
          class="flex gap-component-5 items-center cursor-pointer"
          (click)="op.toggle($event)"
          [attr.aria-label]="'NAVBAR.USER_MENU' | translate"
        >
          <div class="relative shrink-0">
            <i class="pi pi-user text-[1.5rem]!" aria-hidden="true"></i>
            <div
              class="bg-success rounded-full absolute left-3 bottom-5 size-sizing-component-7 grid place-items-center"
            >
              <img
                src="/assets/icons/checkmark.svg"
                class="size-sizing-component-6"
                alt=""
                aria-hidden="true"
              />
            </div>
          </div>
          <p class="text-microcopy hidden md:block" aria-hidden="true">{{ user()?.name }}</p>
        </button>
        <!-- Popover -->
        <p-popover
          #op
          [pt]="{
            root: {
              class: 'max-w-118 w-full',
            },
            content: {
              class: 'p-0!',
            },
          }"
          appendTo="body"
        >
          <div class="py-component-6 pl-component-5 pr-component-4 flex justify-between rounded">
            <div class="pr-layout-6">
              <p class="text-subtitle">
                {{ greetingKey() | translate: { name: user()?.name } }}
              </p>
              <p class="text-body-m break-all">{{ user()?.email }}</p>
            </div>
            <p-button
              (click)="op.hide()"
              icon="pi pi-times"
              [attr.aria-label]="'NAVBAR.CLOSE_USER_MENU' | translate"
              variant="text"
              rounded="true"
            ></p-button>
          </div>
          <div class="px-component-5 flex flex-col gap-component-3">
            <div
              class="bg-card px-component-5 py-component-6 flex gap-component-5 justify-between items-center rounded-lg"
            >
              <div class="flex flex-col gap-component-3">
                <p class="text-body-l">{{ 'NAVBAR.LANGUAGE.LABEL' | translate }}</p>
                <p class="text-body-m-bold">
                  {{
                    'NAVBAR.LANGUAGE.CURRENTLY'
                      | translate: { lang: ('NAVBAR.LANGUAGE.NAME' | translate) }
                  }}
                </p>
              </div>
              <p-button
                severity="contrast"
                outlined="true"
                rounded="true"
                (click)="switchLanguage()"
              >
                {{ 'NAVBAR.LANGUAGE.CHANGE' | translate }}
              </p-button>
            </div>
            <div class="bg-card px-component-5 py-component-6 rounded-lg">
              <p class="text-body-l">{{ 'NAVBAR.HELP.LABEL' | translate }}</p>
              <a
                class="text-accent text-body-m flex gap-component-3 py-3.5 mt-1"
                target="_blank"
                rel="noopener noreferrer"
                [href]="urls.userGuideUrl"
                ><span class="underline">{{ 'NAVBAR.HELP.USER_GUIDE' | translate }}</span>
                <i class="pi pi-external-link"></i
              ></a>
              <a
                class="text-accent text-body-m flex gap-component-3 py-3.5"
                target="_blank"
                rel="noopener noreferrer"
                [href]="urls.helpUrl"
                ><span class="underline">{{ 'NAVBAR.HELP.JIRA' | translate }}</span>
                <i class="pi pi-external-link"></i
              ></a>
            </div>
            @if (maintenanceBanner()) {
              <div
                class="bg-warning-subtle px-component-5 py-component-6 rounded-lg flex flex-col gap-component-3"
              >
                <p class="text-body-l">{{ 'NAVBAR.MAINTENANCE.LABEL' | translate }}</p>
                <p class="text-body-s">
                  {{
                    'NAVBAR.MAINTENANCE.TEXT' | translate: maintenanceBanner()!.translationParams
                  }}
                </p>
              </div>
            }
          </div>
          <div class="px-component-5 py-component-6 flex justify-end">
            <p-button
              [label]="'NAVBAR.LOGOUT' | translate"
              severity="contrast"
              rounded="true"
              styleClass="px-3.5 py-2.5"
              (click)="logout()"
            ></p-button>
          </div>
        </p-popover>
        <!-- Hamburger button (mobile only) -->
        <button
          class="md:hidden p-component-3 cursor-pointer"
          (click)="toggleMenu()"
          [attr.aria-expanded]="menuOpen()"
          [attr.aria-label]="'NAVBAR.TOGGLE_NAV' | translate"
        >
          <i [class]="menuOpen() ? 'pi pi-times' : 'pi pi-bars'"></i>
        </button>
      </div>
    </div>
  </div>

  <!-- Mobile dropdown menu -->
  <div
    class="flex flex-col bg-basic md:hidden overflow-hidden transition-all duration-300 ease-in-out border-b border-subtle"
    [class]="menuOpen() ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'"
  >
    @for (route of routes(); track route.path) {
      <a
        class="px-layout-5 py-component-5 text-body-s-bold cursor-pointer hover:bg-subtle"
        [class.text-dominant]="route.isActive"
        [routerLink]="route.path"
        [aria-hidden]="!menuOpen()"
        [attr.tabindex]="!menuOpen() ? -1 : null"
      >
        {{ route.title | translate }}
      </a>
    }
  </div>
</nav>
